// Find & redact: scan the whole document for a phrase or a class of
// sensitive data, review what was found, and mark it.
//
// Marking creates ordinary redaction annotations — the same ones the X
// tool draws — so nothing is destroyed until the file is exported, and
// everything in between (review, deselect, undo, delete a mark) already
// works. That is the safety property worth preserving: a scan can be
// wrong, and a wrong scan must never be the thing that eats content.
//
// It floats beside the document rather than covering it, because
// reviewing hits means looking at the page they came from.
import { useEffect, useMemo, useState } from "react";
import type { ScanHit } from "../lib/protocol.ts";
import {
  REDACT_PATTERNS,
  patternById,
  type ScanRequest,
} from "../lib/redact-patterns.ts";

export interface ScanResults {
  hits: ScanHit[];
  truncated: boolean;
  pagesScanned: number;
}

function patternLabel(id: string): string {
  if (id === "phrase") return "Phrase";
  if (id === "custom") return "Custom";
  return patternById(id)?.label ?? id;
}

export function FindRedactPanel({
  busy,
  results,
  error,
  selected,
  onScan,
  onToggle,
  onSelectAll,
  onJump,
  onMark,
  onClose,
}: {
  busy: boolean;
  /** null until the first scan has run. */
  results: ScanResults | null;
  error: string | null;
  /** Indices into `results.hits`. */
  selected: Set<number>;
  onScan: (req: ScanRequest) => void;
  onToggle: (index: number) => void;
  onSelectAll: (on: boolean) => void;
  onJump: (hit: ScanHit) => void;
  onMark: () => void;
  onClose: () => void;
}) {
  const [phrase, setPhrase] = useState("");
  const [matchCase, setMatchCase] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [patternIds, setPatternIds] = useState<string[]>([]);
  const [customSource, setCustomSource] = useState("");
  const [showCustom, setShowCustom] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const canScan =
    !busy && (phrase.trim().length > 0 || patternIds.length > 0 || customSource.trim().length > 0);

  /** Hits grouped by page, keeping their original indices. */
  const grouped = useMemo(() => {
    const map = new Map<number, { index: number; hit: ScanHit }[]>();
    results?.hits.forEach((hit, index) => {
      const list = map.get(hit.pageIndex) ?? [];
      list.push({ index, hit });
      map.set(hit.pageIndex, list);
    });
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [results]);

  const counts = useMemo(() => {
    const by = new Map<string, number>();
    for (const h of results?.hits ?? []) by.set(h.patternId, (by.get(h.patternId) ?? 0) + 1);
    return [...by.entries()];
  }, [results]);

  const togglePattern = (id: string) =>
    setPatternIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  return (
    <div className="absolute right-6 top-2 z-30 flex max-h-[calc(100%-1rem)] w-[22rem] flex-col rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] shadow-lg">
      <div className="flex items-center gap-2 border-b border-[color:var(--color-border)] px-3 py-2">
        <i className="ri-shield-flash-line text-[color:var(--color-fg-muted)]" aria-hidden />
        <h2 className="text-sm font-semibold">Find &amp; redact</h2>
        <button
          type="button"
          title="Close"
          aria-label="Close find and redact"
          onClick={onClose}
          className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)]"
        >
          <i className="ri-close-line" aria-hidden />
        </button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canScan) onScan({ phrase, matchCase, wholeWord, patternIds, customSource });
        }}
        onKeyDown={(e) => e.stopPropagation()}
        className="space-y-2 border-b border-[color:var(--color-border)] p-3"
      >
        <input
          value={phrase}
          autoFocus
          onChange={(e) => setPhrase(e.target.value)}
          placeholder="Text to redact everywhere…"
          className="h-8 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-xs"
        />
        <div className="flex gap-3 text-[11px] text-[color:var(--color-fg-muted)]">
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={matchCase}
              onChange={(e) => setMatchCase(e.target.checked)}
            />
            Match case
          </label>
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={wholeWord}
              onChange={(e) => setWholeWord(e.target.checked)}
            />
            Whole word
          </label>
        </div>

        <fieldset>
          <legend className="mb-1 text-[11px] font-medium text-[color:var(--color-fg-muted)]">
            Also find
          </legend>
          <div className="grid grid-cols-2 gap-x-2 gap-y-1">
            {REDACT_PATTERNS.map((p) => (
              <label key={p.id} className="flex items-start gap-1 text-[11px]" title={p.hint}>
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={patternIds.includes(p.id)}
                  onChange={() => togglePattern(p.id)}
                />
                <span className="min-w-0">{p.label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {showCustom ? (
          <input
            value={customSource}
            onChange={(e) => setCustomSource(e.target.value)}
            placeholder="Regular expression, e.g. REF-\d{6}"
            className="h-8 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 font-mono text-[11px]"
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowCustom(true)}
            className="text-[11px] text-[color:var(--color-fg-muted)] underline hover:text-[color:var(--color-fg)]"
          >
            Use a regular expression
          </button>
        )}

        <button
          type="submit"
          disabled={!canScan}
          className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] text-xs text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-50"
        >
          {busy ? (
            <i className="ri-loader-4-line animate-spin" aria-hidden />
          ) : (
            <i className="ri-search-line" aria-hidden />
          )}
          {busy ? "Scanning…" : "Scan document"}
        </button>
        {error && <p className="text-[11px] text-red-600">{error}</p>}
      </form>

      {results && (
        <>
          <div className="flex items-center gap-2 border-b border-[color:var(--color-border)] px-3 py-1.5 text-[11px]">
            <span className="font-medium">
              {results.hits.length} match{results.hits.length === 1 ? "" : "es"}
            </span>
            <span className="text-[color:var(--color-fg-muted)]">
              on {grouped.length} of {results.pagesScanned} pages
            </span>
            {results.hits.length > 0 && (
              <button
                type="button"
                onClick={() => onSelectAll(selected.size !== results.hits.length)}
                className="ml-auto underline text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]"
              >
                {selected.size === results.hits.length ? "Clear all" : "Select all"}
              </button>
            )}
          </div>

          {counts.length > 1 && (
            <div className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)] px-3 py-1.5">
              {counts.map(([id, n]) => (
                <span
                  key={id}
                  className="rounded bg-[color:var(--color-bg-subtle)] px-1.5 py-0.5 text-[10px] text-[color:var(--color-fg-muted)]"
                >
                  {patternLabel(id)} · {n}
                </span>
              ))}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {results.hits.length === 0 && (
              <p className="p-2 text-[11px] text-[color:var(--color-fg-muted)]">
                Nothing matched. Note that a match must sit on one line — text broken across a line
                break isn&rsquo;t found — and scanned pages need OCR first.
              </p>
            )}
            {grouped.map(([pageIndex, rows]) => (
              <div key={pageIndex} className="mb-2">
                <p className="px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[color:var(--color-fg-muted)]">
                  Page {pageIndex + 1}
                </p>
                {rows.map(({ index, hit }) => (
                  <div
                    key={index}
                    className="flex items-start gap-1.5 rounded px-1 py-1 hover:bg-[color:var(--color-bg-subtle)]"
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      aria-label={`Redact ${hit.text}`}
                      checked={selected.has(index)}
                      onChange={() => onToggle(index)}
                    />
                    <button
                      type="button"
                      onClick={() => onJump(hit)}
                      className="min-w-0 flex-1 text-left"
                      title="Show on the page"
                    >
                      <span className="block truncate font-mono text-[11px]">{hit.text}</span>
                      <span className="block truncate text-[10px] text-[color:var(--color-fg-muted)]">
                        {patternLabel(hit.patternId)} · {hit.context}
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            ))}
            {results.truncated && (
              <p className="p-2 text-[11px] text-[#e8590c]">
                Stopped at {results.hits.length} matches — there may be more. Mark these, then scan
                again.
              </p>
            )}
          </div>

          <div className="border-t border-[color:var(--color-border)] p-2">
            <button
              type="button"
              disabled={selected.size === 0 || busy}
              onClick={onMark}
              className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-[#e03131] bg-[#e03131] text-xs text-white hover:opacity-90 disabled:opacity-50"
            >
              <i className="ri-checkbox-blank-fill" aria-hidden />
              Mark {selected.size} for redaction
            </button>
            <p className="mt-1 text-center text-[10px] text-[color:var(--color-fg-muted)]">
              Marks are reviewable — content is removed when you download.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
